import { Button } from '@/components/shadcn/button'
import { ComponentRenderer } from '../../AppPage2/ContentPanel/TileGrid/ComponentRenderer'
import { ComponentSizing, TComponentConfig } from '../../AppPage2/types'
import clsx from 'clsx'
import { ChevronDown } from 'lucide-react'
import { Fragment, forwardRef, useCallback, useEffect, useRef, useState, type MutableRefObject } from 'react'
import { TooltipButton } from '@/components/ui/TooltipButton'

export function SnapScrollPanel({
  components,
  containerId,
  sizing = 'viewport',
}: {
  components: TComponentConfig[]
  containerId: string
  sizing?: ComponentSizing
}) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const sectionRefs = useRef<(HTMLDivElement | null)[]>([])
  const [visibleIndex, setVisibleIndex] = useState(0)

  const updateVisibleIndex = useCallback(() => {
    const container = scrollRef.current
    if (!container) return

    const containerTop = container.getBoundingClientRect().top + 1
    let nextVisibleIndex = 0

    sectionRefs.current.forEach((section, index) => {
      if (section && section.getBoundingClientRect().top <= containerTop) {
        nextVisibleIndex = index
      }
    })

    setVisibleIndex(nextVisibleIndex)
  }, [])

  useEffect(() => {
    const container = scrollRef.current
    if (!container) return

    updateVisibleIndex()
    container.addEventListener('scroll', updateVisibleIndex, { passive: true })

    const resizeObserver = new ResizeObserver(updateVisibleIndex)
    sectionRefs.current.forEach((section) => {
      if (section) resizeObserver.observe(section)
    })

    return () => {
      container.removeEventListener('scroll', updateVisibleIndex)
      resizeObserver.disconnect()
    }
  }, [components, updateVisibleIndex])

  const scrollToNext = useCallback(() => {
    const container = scrollRef.current
    const nextSection = sectionRefs.current[visibleIndex + 1]
    if (!container || !nextSection) return

    const top = nextSection.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop
    container.scrollTo({ top, behavior: 'smooth' })
  }, [visibleIndex])

  const showScrollButton = visibleIndex < components.length - 1

  return (
    <div className="relative h-full w-full">
      <div
        ref={scrollRef}
        className={clsx(
          'h-full w-full snap-y snap-mandatory overflow-y-auto scroll-smooth',
          sizing === 'viewport' && 'pr-2',
          sizing === 'content' && 'flex flex-col gap-y-5'
        )}
      >
        {components.map((component, index) => (
          <Fragment key={component.id}>
            <SnapScrollPanelSection
              ref={(element) => {
                sectionRefs.current[index] = element
              }}
              sectionIndex={index}
              component={component}
              containerId={containerId}
              sizing={sizing}
            />
            {sizing === 'content' && index < components.length - 1 && (
              <div aria-hidden className="h-px w-full shrink-0 bg-border" />
            )}
          </Fragment>
        ))}
      </div>
      <ScrollToNextComponentButton onClick={scrollToNext} show={showScrollButton} />
    </div>
  )
}

function ScrollToNextComponentButton({ show, onClick }: { show: boolean; onClick: () => void }) {
  return (
    <div
      className={clsx(
        'pointer-events-none absolute -inset-x-3 -bottom-2 z-10 flex justify-end transition-opacity duration-200',
        show ? 'opacity-100' : 'opacity-0'
      )}
    >
      <TooltipButton tooltip="Scroll to next component">
        <div className="relative size-10">
          <span
            aria-hidden
            className={clsx(
              'absolute inset-0 rounded-full bg-foreground/20 motion-reduce:animate-none',
              show && 'animate-subtle-pulse'
            )}
          />
          <Button
            aria-label="Scroll to next component"
            className={clsx(
              'relative z-10 size-10 !rounded-full bg-offwhite shadow-lg transition-none',
              show ? 'pointer-events-auto' : 'pointer-events-none'
            )}
            onClick={onClick}
            size="icon"
            type="button"
            variant="outline"
          >
            <ChevronDown className="size-5" />
          </Button>
        </div>
      </TooltipButton>
    </div>
  )
}

const SnapScrollPanelSection = forwardRef<
  HTMLDivElement,
  {
    component: TComponentConfig
    containerId: string
    sectionIndex: number
    sizing: ComponentSizing
  }
>(function SnapScrollPanelSection({ component, containerId, sectionIndex, sizing }, forwardedRef) {
  const tileContainerRef = useRef<HTMLDivElement | null>(null) as MutableRefObject<HTMLDivElement | null>

  const setContainerRef = useCallback(
    (element: HTMLDivElement | null) => {
      tileContainerRef.current = element
      if (typeof forwardedRef === 'function') {
        forwardedRef(element)
      } else if (forwardedRef) {
        ;(forwardedRef as MutableRefObject<HTMLDivElement | null>).current = element
      }
    },
    [forwardedRef]
  )

  return (
    <div
      className={clsx(
        'w-full shrink-0 snap-start',
        sizing === 'viewport' ? 'h-full snap-always overflow-hidden' : 'h-auto overflow-visible'
      )}
      data-section-index={sectionIndex}
      ref={setContainerRef}
    >
      <div className={clsx('flex flex-col gap-y-5 [&>div]:p-0', sizing === 'viewport' ? 'size-full' : 'w-full')}>
        <ComponentRenderer
          component={component}
          containerId={containerId}
          sizing={sizing}
          tileContainerRef={tileContainerRef}
        />
      </div>
    </div>
  )
})
